/**
 * The field types and helpers for the content documents in `/content`. The documents keep
 * the document shape the site was originally exported with, so these types describe that
 * shape and the helpers read it. Nothing here fetches or validates.
 */

// ---------------------------------------------------------------------------
// Rich text
// ---------------------------------------------------------------------------

export type RTSpanType = 'strong' | 'em' | 'hyperlink' | 'label'

export interface RTSpan {
  type: RTSpanType
  start: number
  end: number
  data?: RTLinkNodeData
  label?: string
}

export type RTBlockType =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6'
  | 'paragraph'
  | 'preformatted'
  | 'list-item'
  | 'o-list-item'
  | 'embed'

export interface RTTextNode {
  type: Exclude<RTBlockType, 'embed'>
  text: string
  spans: RTSpan[]
  direction?: string
}

export interface RTImageNode {
  type: 'image'
  id: string
  url: string
  alt: string | null
  copyright: string | null
  dimensions: { width: number; height: number }
  edit?: unknown
  linkTo?: RTLinkNodeData
}

export type RTNode = RTTextNode | RTImageNode

export type RichTextField = RTNode[]
export type TitleField = RichTextField

// ---------------------------------------------------------------------------
// Link
// ---------------------------------------------------------------------------

export interface RTLinkNodeData {
  link_type?: string
  url?: string
  target?: string
  type?: string
  uid?: string
  text?: string
  [key: string]: unknown
}

export type LinkField = RTLinkNodeData

// ---------------------------------------------------------------------------
// Content relationship (a Link field pointing at a document)
// ---------------------------------------------------------------------------

export type ContentRelationshipField<TypeEnum extends string = string> = RTLinkNodeData & { type?: TypeEnum }

// ---------------------------------------------------------------------------
// Image
// ---------------------------------------------------------------------------

export interface ImageFieldImage {
  dimensions: { width: number; height: number }
  alt: string | null
  url: string
}

export type ImageField<ThumbnailNames extends string = never> = Partial<ImageFieldImage> &
  Record<ThumbnailNames, ImageFieldImage>

// ---------------------------------------------------------------------------
// Scalar fields
// ---------------------------------------------------------------------------

export type KeyTextField = string | null
export type DateField = string | null
export type TimestampField = string | null
export type BooleanField = boolean
export type NumberField = number | null
export type ColorField = string | null
export type SelectField<
  Choices extends string = string,
  State extends 'empty' | 'filled' = FieldState,
> = State extends 'filled' ? Choices : Choices | null
export type GroupField<Item = Record<string, unknown>> = Item[]
export type EmbedField = Record<string, unknown> | null
export type GeoPointField = { latitude: number; longitude: number } | null

export type FieldState = 'empty' | 'filled'

/** A "repeat" Link field, which is always an array in the documents. */
export type Repeatable<T> = T[]

// ---------------------------------------------------------------------------
// Slices / documents
// ---------------------------------------------------------------------------

export interface Slice<SliceType extends string = string, Primary = Record<string, unknown>> {
  id: string
  slice_type: SliceType
  variation: string
  primary: Primary
}

export type SharedSlice<
  SliceType extends string = string,
  Variation = { variation: string; primary: Record<string, unknown> },
> = Slice<SliceType> & Variation

export interface SharedSliceVariation<
  VariationName extends string = string,
  Primary = Record<string, unknown>,
> {
  id?: string
  slice_type?: string
  variation: VariationName
  primary: Primary
}

export type SliceZone<TSlice = Slice> = TSlice[]

export interface ContentDocument<Data = Record<string, unknown>, Type extends string = string> {
  id: string
  uid: string | null
  type: Type
  last_publication_date: string
  data: Data
}

export type ContentDocumentWithUID<
  Data = Record<string, unknown>,
  Type extends string = string,
> = ContentDocument<Data, Type> & { uid: string }

export type ContentDocumentWithoutUID<
  Data = Record<string, unknown>,
  Type extends string = string,
> = ContentDocument<Data, Type> & { uid: null }

// ---------------------------------------------------------------------------
// Runtime helpers (asText / asDate / isFilled)
// ---------------------------------------------------------------------------

/** Concatenates the text of every block in a rich text field, one block per line. */
export function asText(field: RichTextField | null | undefined): string {
  if (!field || !Array.isArray(field)) return ''
  return field.map((node) => ('text' in node ? node.text : '')).join('\n')
}

/** Parses a Date/Timestamp field into a JS Date, or null if empty. */
export function asDate(field: string | null | undefined): Date | null {
  if (!field) return null
  const d = new Date(field)
  return Number.isNaN(d.getTime()) ? null : d
}

function isRichTextFilled(field: unknown): boolean {
  if (!Array.isArray(field) || field.length === 0) return false
  if (field.length === 1) {
    const node = field[0] as { type?: string; text?: string }
    if (node && node.type !== 'image' && (!node.text || node.text.trim() === '')) return false
  }
  return true
}

function isLinkFilled(field: unknown): boolean {
  if (!field || typeof field !== 'object') return false
  const link = field as RTLinkNodeData
  if (!link.link_type || link.link_type === 'Any') return false
  return Boolean(link.url || link.id || link.uid)
}

function isImageFilled(field: unknown): boolean {
  if (!field || typeof field !== 'object') return false
  return Boolean((field as ImageFieldImage).url)
}

export const isFilled = {
  richText: isRichTextFilled,
  link: isLinkFilled,
  image: isImageFilled,
  keyText: (field: unknown): field is string => typeof field === 'string' && field.length > 0,
  select: <T>(field: T | null | undefined): field is T => field !== null && field !== undefined,
  group: (field: unknown): field is unknown[] => Array.isArray(field) && field.length > 0,
  number: (field: unknown): field is number => typeof field === 'number',
  boolean: (field: unknown): field is boolean => typeof field === 'boolean',
  color: (field: unknown): field is string => typeof field === 'string' && field.length > 0,
  date: (field: unknown): field is string => typeof field === 'string' && field.length > 0,
  timestamp: (field: unknown): field is string => typeof field === 'string' && field.length > 0,
  embed: (field: unknown): boolean => Boolean(field),
  contentRelationship: isLinkFilled,
  geoPoint: (field: unknown): boolean => Boolean(field),
}
